import Fuse from "fuse.js"
import equals from "ramda/src/equals"
import getPath from "ramda/src/path"

export const fuzzy = (data, {returnPath = ["item"], ...opts} = {}) => {
  const fuse = new Fuse(data, opts)

  // Slice pattern because the docs warn that it'll crash if too long
  return pattern => {
    const results = pattern
      ? fuse.search(pattern.slice(0, 32))
      : data.map((item, refIndex) => ({item, refIndex}))

    return results.map(getPath(returnPath))
  }
}

export const memoizeActionCreator = fn => {
  let key, value

  return (...args) => {
    const newKey = JSON.stringify(args)

    if (!equals(key, newKey)) {
      key = newKey

      return dispatch => {
        value = dispatch(fn(...args))

        return value
      }
    }

    return dispatch => value
  }
}

export const throwIf = err => {
  if (err) {
    throw err
  }
}

export const offlineErrors = [
  "Failed to fetch",
  "TypeError: Failed to fetch",
  "The request timed out.",
  "The network connection was lost.",
  "Could not connect to the server.",
]

export const poll = ({makeRequest, isComplete, isError}) => {
  return new Promise((resolve, reject) => {
    let nErrors = 0

    ;(function check() {
      setTimeout(async () => {
        try {
          const res = await makeRequest()

          if (isComplete(res)) {
            resolve(res)
          } else if (isError && isError(res)) {
            reject(res)
          } else {
            check()
          }
        } catch (e) {
          // If it's a network error, keep trying
          if (offlineErrors.includes(e.message) && nErrors < 20) {
            check()

            nErrors += 1

            return
          }

          throw e
        }
      }, 2000)
    })()
  })
}

export const resolve = x => Promise.resolve(x)
export const reject = x => Promise.reject(x)

export const cancelableThunk = (cachedResult, thunk) => {
  if (!window.AbortController) {
    return thunk
  }

  let ctrl = new AbortController()

  const f =
    (...args) =>
    async dispatch => {
      f.abort()

      let result
      try {
        result = await dispatch(thunk(...args, ctrl.signal))
      } catch (e) {
        result = cachedResult
      }

      cachedResult = result

      return result
    }

  f.abort = () => {
    ctrl.abort()
    ctrl = new AbortController()
  }

  return f
}

export const debouncedThunk = (timeout, thunk) => {
  let sentinel, promise

  return (...args) =>
    dispatch => {
      const currentSentinel = Math.random()

      sentinel = currentSentinel
      promise = new Promise(resolve => {
        setTimeout(() => {
          if (currentSentinel === sentinel) {
            resolve(dispatch(thunk(...args)))
          } else {
            resolve(promise)
          }
        }, timeout)
      })

      return promise
    }
}

export const showLoading = async ({load, start}) => {
  let asyncDone = false

  setTimeout(() => asyncDone || start(), 10)

  let result, error
  try {
    result = await load()
  } catch (err) {
    error = err
  }

  asyncDone = true

  if (error) {
    throw error
  }

  return result
}

export const noopThunk = () => () => null

// Deprecated

export const defineEnum = xs => {
  xs.forEach(x => {
    xs[x.toUpperCase()] = x
  })

  return xs
}

export const definePrefixedEnum = (prefix, xs) => {
  xs.forEach(x => {
    xs[x.toUpperCase()] = `${prefix}${x}`
  })

  return xs
}
